// components/StatsSection.tsx
import React from 'react';
import { motion, Variants } from 'framer-motion';
import { 
  FaUsers, 
  FaHandHoldingHeart, 
  FaBuilding, 
  FaChartLine,
  FaRocket
} from '../../../../utils/iconUtils';
import { HomeStats } from '../components/types';

interface StatsSectionProps {
  stats: HomeStats;
}

const StatsSection: React.FC<StatsSectionProps> = ({ stats }) => {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  const statItems = [
    { 
      value: '3,000+', 
      label: 'Beneficiaries', 
      icon: FaUsers, 
      gradient: 'from-emerald-500 to-green-500',
      description: 'Lives transformed'
    },
    { 
      value: '20+', 
      label: 'Active Programs', 
      icon: FaHandHoldingHeart, 
      gradient: 'from-blue-500 to-cyan-500',
      description: 'Initiatives running'
    },
    { 
      value: '50+', 
      label: 'Communities', 
      icon: FaBuilding, 
      gradient: 'from-violet-500 to-purple-500',
      description: 'Areas reached'
    },
    { 
      value: '10+', 
      label: 'Years of Service', 
      icon: FaChartLine, 
      gradient: 'from-rose-500 to-pink-500',
      description: 'Dedicated service'
    },
  ];

  return (
<motion.div 
  className="py-20 relative overflow-visible"
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true }}
  variants={containerVariants}
>
      {/* GRADIENT 2: Professional Navy/Indigo */}
  {/* <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-indigo-900 to-slate-900">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
    </div>
  </div>
       */}
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          className="text-center mb-16"
          variants={itemVariants}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full text-sm font-semibold mb-4 backdrop-blur-sm">
            <FaRocket className="text-xs" />
            OUR IMPACT
          </span>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Making a <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Difference</span> Every Day
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Through dedicated programs and community initiatives that transform lives
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statItems.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ 
                y: -8,
                transition: { type: "spring", stiffness: 300 }
              }}
              className="group relative"
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${stat.gradient} rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500`}></div>
              
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 text-center border border-gray-700/50 group-hover:border-transparent transition-all duration-500">
                <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${stat.gradient} mb-6 shadow-lg`}>
                  <stat.icon className="text-2xl text-white" />
                </div>
                
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-lg font-semibold text-gray-300 mb-2">{stat.label}</div>
                <div className="text-sm text-gray-400">{stat.description}</div>
                
                <div className={`mt-4 w-12 h-1 bg-gradient-to-r ${stat.gradient} rounded-full mx-auto opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default StatsSection;